import React, { useRef, useEffect, RefObject } from 'react';
import PropTypes from 'prop-types';

import AccountList from './account-list';
import ArrowDown from './icons/ArrowDown';
import { Profile } from '@finn-no/profile-client';
import { PersonalAccountProps } from './account';
import { Organisation } from '@finn-no/organisation-http-client';

function useOutsideAlerter(
    ref: RefObject<HTMLDivElement>,
    callback: (value: boolean) => void,
) {
    useEffect(() => {
        function handleClickOutside(event: Event) {
            if (ref.current && !ref.current.contains(event.target as Node)) {
                callback(true);
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleClickOutside);
        };
    }, [ref]);
}

export type AccountDropdownProps = {
    organisations: Organisation[];
    profile: Profile;
    activeOrganisation?: Organisation;
    isAccountMenuOpen: boolean;
    handleAccountMenuToggle: (value?: boolean) => void;
    track: (name: string, value?: unknown) => void;
};
export default function AccountDropdown({
    organisations,
    profile,
    activeOrganisation,
    isAccountMenuOpen,
    handleAccountMenuToggle,
    track,
}: AccountDropdownProps) {
    const dropdownClass = isAccountMenuOpen
        ? 'account-dropdown account-dropdown-active rounded-lg'
        : 'account-dropdown';

    const accountMenuClass = isAccountMenuOpen
        ? 'top-bar__account-menu top-bar__account-menu__selected'
        : 'top-bar__account-menu';

    const wrapperRef = useRef<HTMLDivElement>(null);
    useOutsideAlerter(wrapperRef, handleAccountMenuToggle);

    return (
        <div ref={wrapperRef} className={accountMenuClass}>
            <button
                type="button"
                className="top-bar__account-menu__button"
                onClick={() => handleAccountMenuToggle()}
                aria-expanded={isAccountMenuOpen}
            >
                <span className="top-bar__account-menu__button__title">
                    {activeOrganisation ? activeOrganisation.displayName : ''}
                </span>
                <span className="top-bar__menu-item__arrow">
                    <ArrowDown />
                </span>
            </button>

            <div className={dropdownClass}>
                <div className="account-dropdown__content">
                    <AccountList
                        organisations={organisations}
                        profile={profile as unknown as PersonalAccountProps}
                        track={track}
                    />
                </div>
            </div>
        </div>
    );
}

AccountDropdown.defaultProps = {
    accounts: [],
    profile: undefined,
    isAccountMenuOpen: false,
    handleAccountMenuToggle: () => undefined,
    track: () => undefined,
    activeAccount: undefined,
};

AccountDropdown.propTypes = {
    accounts: PropTypes.arrayOf(PropTypes.object),
    profile: PropTypes.shape({
        displayName: PropTypes.string,
        image: PropTypes.string,
    }),
    activeAccount: PropTypes.shape({
        market: PropTypes.string,
        orgId: PropTypes.number,
        displayName: PropTypes.string,
        displayLogo: PropTypes.shape({
            medium: PropTypes.string,
        }),
    }),
    isAccountMenuOpen: PropTypes.bool,
    handleAccountMenuToggle: PropTypes.func,
    track: PropTypes.func,
};
